import { QueryBooleanSchema } from 'notable-common';
import { z } from 'zod';
/**
 * Which entity a theme hangs off, as a filter.
 *
 * None of the three is required: the routes that take this accept an empty selector and resolve it
 * themselves, either by inheritance or by rejecting the request in the handler.
 */
export const PracticeThemeEntityQuerySchema = z
    .object({
    practiceId: z.uuid().optional(),
    practiceGroupId: z.uuid().optional(),
    organizationId: z.uuid().optional(),
})
    .strict();
/** The attributes a theme carries, each nullable: null is how an inherited value is cleared. */
export const PracticeThemeValuesSchema = z
    .object({
    primaryHexColor: z.string().min(1).nullable().optional(),
    secondaryHexColor: z.string().min(1).nullable().optional(),
    passbookTextColor: z.string().min(1).nullable().optional(),
    passbookLogoUrl: z.string().min(1).nullable().optional(),
    previsitLogoUrl: z.string().min(1).nullable().optional(),
    previsitDomain: z.string().min(1).nullable().optional(),
    previsitFaviconUrl: z.string().min(1).nullable().optional(),
    previsitPageTitle: z.string().min(1).nullable().optional(),
    // The empty string is allowed here and nowhere else: it is how a terms document is removed.
    termsOfUseGsPath: z.string().nullable().optional(),
    mastheadImageUrl: z.string().min(1).nullable().optional(),
    // `Joi.boolean()` reads the word as the value, and the house schema is how we say that.
    ssoEnabled: QueryBooleanSchema.nullable().optional(),
    ssoButtonLabel: z.string().min(1).nullable().optional(),
    ssoButtonIconUrl: z.string().min(1).nullable().optional(),
})
    .strict();
/** The theme attribute names, for a route that asks which entity a single attribute comes from. */
export const PracticeThemeAttributeSchema = PracticeThemeValuesSchema.keyof();
export const PracticeThemeCreateSchema = PracticeThemeEntityQuerySchema.extend({
    theme: PracticeThemeValuesSchema.optional(),
}).strict();
/** A patch: at least one attribute, since an empty body would be a no-op write. */
export const PracticeThemeUpdateSchema = PracticeThemeValuesSchema.refine((values) => Object.keys(values).length > 0, { message: 'At least one field must be provided' });
export const PracticeThemeIdParamsSchema = z.object({ practiceThemeId: z.uuid() }).strict();
export const PracticeThemePracticeParamsSchema = z.object({ practiceId: z.uuid() }).strict();
/** Asks which entity in the inheritance chain supplies one attribute of a practice's theme. */
export const PracticeThemeInheritanceOwnerQuerySchema = z
    .object({ attribute: PracticeThemeAttributeSchema })
    .strict();
/**
 * Names the image being replaced, alongside the entity it belongs to. The entity is not optional
 * here even though each id is: the body has to carry the attribute and one of the three.
 */
export const PracticeThemeImageUploadSchema = PracticeThemeEntityQuerySchema.extend({
    attribute: z.string().min(1),
})
    .strict()
    .refine((values) => Object.keys(values).length >= 2, {
    message: 'An entity must be named alongside the attribute',
});
/** The terms document belongs to exactly one entity, not to a combination of them. */
export const PracticeThemeTermsUploadSchema = PracticeThemeEntityQuerySchema.refine((values) => Object.keys(values).length === 1, { message: 'Exactly one of practiceId, practiceGroupId or organizationId is required' });
//# sourceMappingURL=theme.js.map